/**
 * Performance optimization utilities
 */

/**
 * Debounces a function to prevent rapid firing
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>): void {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Throttles a function to limit execution frequency
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  let lastFunc: ReturnType<typeof setTimeout> | null = null;
  let lastRan = 0;
  
  return function(...args: Parameters<T>): void {
    if (!inThrottle) {
      func(...args);
      lastRan = Date.now();
      inThrottle = true;
      
      setTimeout(() => {
        inThrottle = false;
        if (lastFunc) {
          clearTimeout(lastFunc);
          lastFunc = null;
        }
      }, limit);
    } else {
      if (lastFunc) clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func(...args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

/**
 * Schedules heavy tasks to avoid blocking the main thread
 */
export function scheduleTask<T>(
  items: T[],
  processFn: (item: T) => void,
  chunkSize = 50,
  delay = 0
): void {
  let index = 0;
  
  function processChunk() {
    const end = Math.min(index + chunkSize, items.length);
    
    while (index < end) {
      processFn(items[index]);
      index++;
    }
    
    if (index < items.length) {
      setTimeout(processChunk, delay);
    }
  }
  
  processChunk();
}

/**
 * Adds passive event listener option when supported
 */
export function addPassiveEventListener(
  element: HTMLElement | Window | Document,
  eventType: string,
  handler: EventListenerOrEventListenerObject,
  passive = true
): void {
  let options: boolean | AddEventListenerOptions = false;
  
  try {
    options = {
      passive,
      capture: false
    };
    
    element.addEventListener(eventType, handler, options);
  } catch (e) {
    element.addEventListener(eventType, handler, false);
  }
}

/**
 * Request animation frame with fallback
 */
export const nextFrame = (callback: FrameRequestCallback): number => {
  return window.requestAnimationFrame(callback);
}

/**
 * Cancel animation frame with fallback
 */
export const cancelFrame = (id: number): void => {
  window.cancelAnimationFrame(id);
}

/**
 * Performs a DOM read-then-write batch to avoid layout thrashing
 */
export function batchDOMOperation<T>(
  elements: HTMLElement[],
  readFn: (el: HTMLElement) => T,
  writeFn: (el: HTMLElement, readValue: T) => void
): void {
  // Read phase - collect all values
  const values = elements.map(readFn);
  
  // Write phase - apply all updates
  elements.forEach((el, i) => writeFn(el, values[i]));
} 